"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, User, Folder } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardTitle, CardFooter } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="h-[400px] animate-pulse bg-card border-border shadow-md">
            <div className="h-48 bg-muted w-full rounded-t-md" />
            <CardContent className="p-6">
              <div className="h-4 bg-muted w-1/4 mb-4 rounded" />
              <div className="h-6 bg-muted w-3/4 mb-2 rounded" />
              <div className="h-6 bg-muted w-1/2 rounded" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Failed to load policy updates. Please try again later.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>No policy shifts recorded yet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="flex flex-col h-full border border-border shadow-md bg-card overflow-hidden rounded-md transition-shadow hover:shadow-lg">
          {post.featuredImage && <Link to={post.link || "#"} className="block overflow-hidden h-48 shrink-0">
              <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
            </Link>}
          
          <CardContent className="flex-1 p-6 flex flex-col">
            <div className="flex items-center gap-2 text-[14px] text-primary mb-3 font-mono font-medium">
              <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
              <time dateTime={post.date}>{post.date || "Recent"}</time>
            </div>
            
            <Link to={post.link || "#"} className="group">
              <CardTitle className="font-default text-xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-3 tracking-tight">
                {post.title}
              </CardTitle>
            </Link>
            
            {post.excerpt && <div className="mt-3 text-muted-foreground font-default text-sm line-clamp-2 leading-relaxed" dangerouslySetInnerHTML={{
          __html: post.excerpt
        }} />}
          </CardContent>

          <CardFooter className="px-6 py-4 border-t border-border flex items-center justify-between text-[14px] text-muted-foreground font-mono bg-muted/30 mt-auto">
            <div className="flex items-center gap-1.5">
              <User className="size-4 text-primary" />
              <span className="truncate max-w-[100px]">{post.author?.name || "FINCALC"}</span>
            </div>
            
            {post.categories && post.categories.length > 0 && <div className="flex items-center gap-1.5">
                <Folder className="size-4 text-primary" />
                <span className="truncate max-w-[100px]">{post.categories[0].name}</span>
              </div>}
          </CardFooter>
        </Card>)}
    </div>;
}
export default function PerformanceInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="224098" id="latest-policy-shifts" className="bg-background text-foreground py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tighter">
            Latest Policy Shifts
          </h2>
          <p className="font-default text-muted-foreground max-w-2xl mx-auto text-lg">
            Stay informed on Ontario tax laws, TFSA contribution limits, and the latest rent increase guidelines.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}